<template>
  <div>

    <!-- Template list -->
    <template v-if="!current">
      <div class="d-flex flex-wrap align-items-start justify-content-between mb-3">
        <div class="mr-3">
          <h4 class="font-weight-bold mb-1">Rapportmaler</h4>
          <p class="text-muted small mb-0">
            Maler for sjekklister og rapporter som fylles ut under fanen «Rapporter» på ordren.
          </p>
        </div>

        <div class="d-flex mt-2 mt-md-0">
          <label
            class="btn btn-outline-secondary mb-0 mr-2"
            title="Importer en mal fra en JSON-fil"
          >
            <i class="ion ion-ios-cloud-upload mr-1"></i> Importer JSON
            <input
              type="file"
              accept=".json,application/json"
              class="d-none"
              @change="importTemplate"
            />
          </label>

          <b-button
            variant="primary"
            @click="openTemplate(null)"
          ><i class="ion ion-ios-add mr-1"></i> Ny mal</b-button>
        </div>
      </div>

      <b-card no-body>
        <b-table
          show-empty
          stacked="sm"
          class="mb-0 template-table"
          :items="templates"
          :fields="fields"
          :busy="isLoading"
          sort-by="name"
        >
          <template #table-busy>
            <div class="text-center text-muted py-4">
              <b-spinner small class="mr-2"></b-spinner> Laster maler...
            </div>
          </template>

          <template #empty>
            <div class="text-center py-5">
              <i class="ion ion-ios-document text-muted template-empty-icon"></i>
              <h6 class="mt-2 mb-1">Ingen rapportmaler ennå</h6>
              <p class="text-muted small mb-0">Opprett en ny mal, eller importer en mal fra en JSON-fil.</p>
            </div>
          </template>

          <template #cell(name)="row">
            <b-link
              class="font-weight-bold"
              @click="openTemplate(row.item)"
            >{{ row.item.name }}</b-link>
            <div
              v-if="row.item.description"
              class="text-muted small template-description"
              :title="row.item.description"
            >{{ row.item.description }}</div>
          </template>

          <template #cell(content)="row">
            <div class="text-nowrap">
              {{ row.item.schema.sections.length }} {{ row.item.schema.sections.length === 1 ? 'seksjon' : 'seksjoner' }}
              · {{ countFields(row.item.schema) }} felter
            </div>
            <b-badge
              v-if="countRequired(row.item.schema)"
              variant="outline-danger"
              class="mt-1"
            >{{ countRequired(row.item.schema) }} påkrevde</b-badge>
          </template>

          <template #cell(updatedAt)="row">
            <span class="text-nowrap">{{ $date(row.item.updatedAt).format("DD.MM.YYYY") }}</span>
          </template>

          <template #cell(actions)="row">
            <div class="text-nowrap">
              <b-button
                variant="primary"
                size="sm"
                class="mr-1"
                @click="openTemplate(row.item)"
              ><i class="ion ion-ios-create mr-1"></i>Rediger</b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mr-1"
                title="Lag en kopi av malen"
                @click="duplicateTemplate(row.item)"
              ><i class="ion ion-ios-copy mr-1"></i>Kopier</b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                title="Last ned malen som JSON-fil (kan importeres i andre installasjoner)"
                @click="exportTemplate(row.item)"
              ><i class="ion ion-ios-download mr-1"></i>Eksporter JSON</b-button>
            </div>
          </template>
        </b-table>
      </b-card>
    </template>

    <!-- Template editor -->
    <template v-else>
      <b-row class="mb-3">
        <b-col md="8">
          <h4 class="font-weight-bold ml-1">
            <b-link
              class="mr-2"
              @click="current = null"
            ><i class="ion ion-ios-arrow-back"></i></b-link>
            {{ current.id ? 'Rediger mal' : 'Ny mal' }}
          </h4>
        </b-col>

        <b-col md="4">
          <b-button
            class="float-right"
            variant="primary"
            :disabled="saving"
            @click="saveTemplate()"
          >{{ saving ? 'Lagrer...' : 'Lagre mal' }}</b-button>

          <b-button
            v-if="current.id"
            class="float-right mr-2"
            variant="outline-danger"
            v-b-modal.deleteTemplate
          >Slett</b-button>
        </b-col>
      </b-row>

      <b-card class="mb-3">
        <b-row>
          <b-col md="6">
            <b-form-group label="Navn *">
              <b-form-input
                v-model="current.name"
                placeholder="f.eks. Sjekkliste avlusning"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Beskrivelse">
              <b-form-input
                v-model="current.description"
                placeholder="Valgfri beskrivelse"
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <small class="text-muted">
          {{ current.schema.sections.length }} seksjoner · {{ countFields(current.schema) }} felter
          <span v-if="current.id">· Endringer gjelder nye rapporter. Rapporter som allerede er opprettet beholder malen de ble laget fra.</span>
        </small>
      </b-card>

      <b-card class="mb-3">
        <h6 class="font-weight-bold mb-1">Standardverdier fra ordren</h6>
        <p class="text-muted small mb-2">
          Felter kan fylles ut automatisk med data fra ordren når rapporten opprettes.
          Skriv plassholdere i krøllparenteser i «Standardverdi», f.eks. <code>{ordrenummer} – {lokalitet}</code>,
          eller klikk i et «Standardverdi»-felt og velg en plassholder under.
        </p>
        <b-button
          v-for="p in placeholders"
          :key="p.key"
          size="sm"
          variant="outline-primary"
          class="mr-1 mb-1"
          :title="p.text"
          @mousedown.prevent
          @click="insertPlaceholder(p.key)"
        >{{ '{' + p.key + '}' }}</b-button>
      </b-card>

      <b-card
        v-for="(section, si) in current.schema.sections"
        :key="si"
        class="mb-3"
      >
        <div class="d-flex align-items-center mb-3">
          <b-form-input
            v-model="section.title"
            class="font-weight-bold mr-2"
            placeholder="Seksjonstittel"
          ></b-form-input>
          <b-button-group size="sm">
            <b-button
              variant="outline-secondary"
              title="Flytt opp"
              :disabled="si === 0"
              @click="move(current.schema.sections, si, -1)"
            >↑</b-button>
            <b-button
              variant="outline-secondary"
              title="Flytt ned"
              :disabled="si === current.schema.sections.length - 1"
              @click="move(current.schema.sections, si, 1)"
            >↓</b-button>
            <b-button
              variant="outline-secondary"
              title="Kopier seksjon"
              @click="duplicate(current.schema.sections, si, 'title')"
            >Kopier</b-button>
            <b-button
              variant="outline-danger"
              title="Slett seksjon"
              @click="current.schema.sections.splice(si, 1)"
            >Slett</b-button>
          </b-button-group>
        </div>

        <div
          v-for="(field, fi) in section.fields"
          :key="fi"
          class="report-field p-2 mb-2 border rounded"
        >
          <b-row>
            <b-col md="4">
              <b-form-input
                v-model="field.label"
                size="sm"
                placeholder="Feltnavn"
              ></b-form-input>
            </b-col>
            <b-col md="3">
              <b-form-select
                v-model="field.type"
                size="sm"
                :options="fieldTypes"
              ></b-form-select>
            </b-col>
            <b-col md="3">
              <b-form-input
                v-if="field.type === 'select'"
                v-model="field.options"
                size="sm"
                placeholder="Valg, separert med komma"
              ></b-form-input>
              <b-form-input
                v-else-if="field.type === 'table'"
                v-model="field.columns"
                size="sm"
                placeholder="Kolonner, separert med komma"
              ></b-form-input>
              <b-form-checkbox
                v-model="field.required"
                :class="{ 'mt-1': field.type === 'select' || field.type === 'table' }"
              >Påkrevd</b-form-checkbox>
            </b-col>
            <b-col
              md="2"
              class="text-right"
            >
              <b-button-group size="sm">
                <b-button
                  variant="outline-secondary"
                  title="Flytt opp"
                  :disabled="fi === 0"
                  @click="move(section.fields, fi, -1)"
                >↑</b-button>
                <b-button
                  variant="outline-secondary"
                  title="Flytt ned"
                  :disabled="fi === section.fields.length - 1"
                  @click="move(section.fields, fi, 1)"
                >↓</b-button>
                <b-button
                  variant="outline-secondary"
                  title="Kopier felt"
                  @click="duplicate(section.fields, fi, 'label')"
                >⧉</b-button>
                <b-button
                  variant="outline-danger"
                  title="Slett felt"
                  @click="section.fields.splice(fi, 1)"
                >✕</b-button>
              </b-button-group>
            </b-col>
          </b-row>
          <b-input-group
            v-if="defaultTypes.includes(field.type)"
            size="sm"
            class="mt-2"
          >
            <b-input-group-prepend
              is-text
              title="Fylles inn automatisk med data fra ordren når rapporten opprettes"
            >Standardverdi</b-input-group-prepend>
            <b-form-input
              v-model="field.default"
              :placeholder="field.type === 'date' ? 'f.eks. {startdato} eller {dagens_dato}' : 'f.eks. {ordrenummer} – {lokalitet}'"
              @focus="activeField = field"
            ></b-form-input>
          </b-input-group>
          <b-form-textarea
            v-model="field.hint"
            size="sm"
            class="mt-2"
            rows="1"
            max-rows="8"
            placeholder="Hjelpetekst (valgfri, vises under feltet i skjemaet)"
          ></b-form-textarea>
        </div>

        <p
          v-if="section.fields.length === 0"
          class="text-muted small"
        >Ingen felter ennå.</p>

        <b-button
          variant="outline-primary"
          size="sm"
          @click="section.fields.push(newField())"
        >+ Legg til felt</b-button>
      </b-card>

      <b-button
        variant="outline-primary"
        block
        @click="current.schema.sections.push(newSection())"
      >+ Legg til seksjon</b-button>

      <b-modal
        id="deleteTemplate"
        title="Slett rapportmal"
        ok-title="Slett"
        ok-variant="danger"
        cancel-title="Avbryt"
        @ok="deleteTemplate()"
      >
        Slette «{{ current.name }}»? Rapporter som allerede er laget fra malen beholdes.
      </b-modal>
    </template>

  </div>
</template>

<script>
import api, { FIELD_TYPES, DEFAULT_TYPES, PLACEHOLDERS, newField, newSection, newSchema, countFields, countRequired, errorText } from "./api";

export default {
  data() {
    return {
      templates: [],
      current: null,
      isLoading: false,
      saving: false,
      activeField: null,
      defaultTypes: DEFAULT_TYPES,
      placeholders: PLACEHOLDERS,
      fieldTypes: FIELD_TYPES,
      fields: [
        { key: "name", label: "Mal", sortable: true, class: "text-left" },
        { key: "content", label: "Innhold" },
        { key: "updatedAt", label: "Sist endret", sortable: true },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },
  methods: {
    newField,
    newSection,
    countFields,
    countRequired,
    toast(message, variant = "danger") {
      this.$bvToast.toast(message, {
        title: variant === "danger" ? "Feil" : "Rapportmaler",
        variant,
        solid: true,
      });
    },
    getTemplates() {
      this.isLoading = true;
      api
        .getTemplates()
        .then((templates) => {
          this.templates = templates;
        })
        .catch((error) => this.toast(errorText(error)))
        .finally(() => {
          this.isLoading = false;
        });
    },
    openTemplate(template) {
      const copy = template
        ? JSON.parse(JSON.stringify(template))
        : { name: "", description: "", schema: newSchema() };
      if (!copy.schema || !Array.isArray(copy.schema.sections)) copy.schema = newSchema();
      copy.schema.sections.forEach((s) => {
        s.fields = (s.fields || []).map((f) => ({ ...newField(), ...f }));
      });
      this.current = copy;
      this.activeField = null;
    },
    insertPlaceholder(key) {
      if (!this.activeField) return this.toast("Klikk i et «Standardverdi»-felt først.");
      const current = this.activeField.default || "";
      this.activeField.default = current + (current && !current.endsWith(" ") ? " " : "") + "{" + key + "}";
    },
    saveTemplate() {
      if (!(this.current.name || "").trim()) return this.toast("Navn er påkrevd.");
      this.saving = true;
      api
        .saveTemplate(this.current)
        .then(() => {
          this.current = null;
          this.getTemplates();
        })
        .catch((error) => this.toast(errorText(error)))
        .finally(() => {
          this.saving = false;
        });
    },
    deleteTemplate() {
      api
        .deleteTemplate(this.current.id)
        .then(() => {
          this.current = null;
          this.getTemplates();
        })
        .catch((error) => this.toast(errorText(error)));
    },
    duplicateTemplate(template) {
      api
        .saveTemplate({ name: template.name + " (kopi)", description: template.description, schema: template.schema })
        .then(() => this.getTemplates())
        .catch((error) => this.toast(errorText(error)));
    },
    // Same JSON format as aquaservices-web-v2, so templates can be moved between the two.
    exportTemplate(template) {
      const json = JSON.stringify(
        { name: template.name, description: template.description || "", schema: template.schema, exportedAt: new Date().toISOString() },
        null,
        2
      );
      const url = URL.createObjectURL(new Blob([json], { type: "application/json;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "rapportmal-" + (template.name || "mal").replace(/[^a-zA-Z0-9æøåÆØÅ _-]/g, "").replace(/\s+/g, "-").toLowerCase() + ".json";
      link.click();
      URL.revokeObjectURL(url);
    },
    importTemplate(event) {
      const file = event.target.files[0];
      if (!file) return;
      file
        .text()
        .then((text) => {
          const parsed = JSON.parse(text);
          if (!parsed.schema || !Array.isArray(parsed.schema.sections)) throw new Error("Ugyldig fil – schema.sections mangler.");
          const name = parsed.name || "Importert mal";
          const taken = this.templates.some((t) => t.name === name);
          return api.saveTemplate({ name: taken ? name + " (import)" : name, description: parsed.description || "", schema: parsed.schema });
        })
        .then(() => {
          this.toast("Malen er importert.", "success");
          this.getTemplates();
        })
        .catch((error) => this.toast(errorText(error)))
        .finally(() => {
          event.target.value = "";
        });
    },
    move(list, index, direction) {
      const target = index + direction;
      if (target < 0 || target >= list.length) return;
      list.splice(target, 0, list.splice(index, 1)[0]);
    },
    duplicate(list, index, nameKey) {
      const copy = JSON.parse(JSON.stringify(list[index]));
      copy[nameKey] = (copy[nameKey] || "") + " (kopi)";
      list.splice(index + 1, 0, copy);
    },
  },
  created() {
    this.getTemplates();
  },
};
</script>

<style lang="css">
.report-field {
  background-color: #f8f9fa;
}

.template-table td {
  vertical-align: middle;
}

.template-description {
  max-width: 640px;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.template-empty-icon {
  font-size: 2.5rem;
}
</style>
